/* eslint-disable complexity */
import React, { FC, Fragment, useMemo } from 'react';
import { Box, ButtonBase, Divider, Drawer, Stack, Typography } from '@mui/material';
import { headerMiniCartItemSX } from '@/components/content/Header/styles/miniCart/item';
import { useCart } from '@/data/Content/Cart';
import { useLocalization } from '@/data/Localization';
import { HeaderMiniCartDropMenuItem } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItem';
import ClearIcon from '@mui/icons-material/Clear';
import { HeaderMiniCartDropMenuItemSkeleton } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItemSkeleton';
import { useOrderItemListRowsAvailability } from '@/data/Content/OrderItemInventory';
import { useNotifications } from '@/data/Content/Notifications';
import { useMobileHeaderQuery } from '@/components/content/Header';
import { Img } from '@/components/blocks/MaterialImage';
import { useBasicCheckOut } from '@/data/Content/BasicCheckOut';
import { OrderSummaryBreakdown } from '@/components/content/Cart/parts/OrderSummaryBreakdown';
import { useUser } from '@/data/User';
import { AuthentificationFlow } from '@/components/blocks/AuthentificationFlow';
import { CatalogEntryIARecommendationFree } from '@/components/content/CatalogEntryIARecommendation/free';
import { ProductType } from '@/data/types/Product';
import { useLockBodyScroll } from '@/utils/lockBodyScroll';
import { getLastAddedChildPartNumber, useIsOpen } from '@/data/Content/HeaderMiniCart';
import {
	cartButtonSX,
	checkoutButtonSX,
	closeIconSX,
	containerSX,
	dividerSX,
	drawerSX,
	footerSX,
	itemsStackSX,
	scrollAreaSX,
	titleImageSX,
	titleSX,
} from '@/components/content/Header/parts/HeaderMiniCartDrawerMenu/styles';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { partNumbersIARecommendationExtractorByUnknown } from '@/utils/partNumbersIARecommendationExtractorByUnknown';
import { useProduct } from '@/data/Content/Product';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { useQuickCartEvents } from '@/data/Content/QuickCartEvents';

const iaEmptyExtractor = (products: ProductType[]) => !products || products.length === 0;

export const HeaderMiniCartDrawerMenu: FC<{
	fromLocal: boolean;
	onClose: () => void;
	onOpenFreeShippingActivatedPopup: (resumeCheckout: () => void) => Promise<boolean>;
	// eslint-disable-next-line complexity
}> = ({ fromLocal, onClose, onOpenFreeShippingActivatedPopup }) => {
	const CartLabels = useLocalization('MiniCart');
	const RouteLabels = useLocalization('Routes');
	const { orderItems, data: order, checkout, canContinue, getCount, router } = useCart();
	const isMobile = useMobileHeaderQuery();
	const MAX_ROWS = isMobile ? 2 : 3;
	const { firstNRows } = useMemo(() => {
		const firstNRows = orderItems?.slice(0, MAX_ROWS);
		const firstNRowsCount = getCount(firstNRows);
		return { firstNRows, firstNRowsCount };
	}, [MAX_ROWS, getCount, orderItems]);

	const lastAddedPartNumber = useMemo(() => getLastAddedChildPartNumber(orderItems), [orderItems]);
	const { trackQuickCartClick } = useQuickCartEvents(lastAddedPartNumber);

	const { product: lastAddedProduct } = useProduct({
		id: lastAddedPartNumber?.split('-T').at(0),
	});

	const allowLastAddedPartNumber = useMemo(() => {
		if (!lastAddedProduct) {
			return undefined;
		}

		const brand = findProductAttribute(lastAddedProduct?.attributes ?? [], 'Marca')
			?.value?.toLowerCase()
			?.trim();

		return brand !== 'on' ? lastAddedProduct.partNumber : undefined;
	}, [lastAddedProduct]);

	useLockBodyScroll(true);

	const { showSuccessMessage } = useNotifications();

	const { totalCoupons, totalDiscount, totalPriceWithoutOffer, orderItemListRowsSorted } =
		useBasicCheckOut({
			adjustment: order?.adjustment || [],
			orderItem: order?.orderItem || [],
			orderId: order?.orderId || '',
		});

	const { grandTotal = '0', totalShippingCharge = '0' } = order || {};

	const {
		orderItemListRowsWithAvailability,
		orderItemListRowsNotAvailable,
		loadingOrderItemListRowsAvailability,
	} = useOrderItemListRowsAvailability(orderItemListRowsSorted);

	const loading =
		orderItemListRowsWithAvailability.length === 0 || loadingOrderItemListRowsAvailability;

	const disabled = loading ? true : canContinue ? !canContinue() : false;

	const { user: { isLoggedIn = false } = {} } = useUser();

	const openAuthFlow = useIsOpen();

	const continueCheckout = () => {
		if (!isLoggedIn) {
			openAuthFlow.on();
			return;
		}

		checkout();
	};

	const handleOnClickCheckout = async () => {
		trackQuickCartClick(orderItemListRowsNotAvailable.length > 0 ? 'cart' : 'checkout');
		if (orderItemListRowsNotAvailable.length > 0) {
			router.push(RouteLabels.Cart.route.t()).finally(() => {
				showSuccessMessage(
					'Revisa tus productos del carrito y elimina el producto caducado para continuar tu compra.',
					'TIENES UN PRODUCTO CADUCADO'
				);
			});

			return;
		}

		if (await onOpenFreeShippingActivatedPopup(continueCheckout)) {
			return;
		}

		continueCheckout();
	};

	const handleOnClickCart = () => {
		trackQuickCartClick('cart');
		const path = globalThis.location.pathname;
		if (path.includes(RouteLabels.Cart.route.t())) {
			onClose();
			return;
		}
		router.push(RouteLabels.Cart.route.t());
	};

	return (
		<>
			<Drawer open onClose={onClose} sx={drawerSX}>
				<Box sx={containerSX}>
					{firstNRows?.length > 0 ? (
						<Fragment>
							<ClearIcon sx={closeIconSX} onClick={onClose} />
							<Box sx={scrollAreaSX}>
								<Typography sx={titleSX}>
									{!fromLocal ? (
										<Img src="/images/check-circle.png" alt="check-circle" sx={titleImageSX} />
									) : null}
									{fromLocal ? 'Carrito de compras' : 'AGREGASTE AL CARRITO'}
								</Typography>
								<Stack spacing={1} sx={itemsStackSX}>
									{!loading ? (
										<React.Fragment>
											{orderItemListRowsWithAvailability.map((product) => (
												<HeaderMiniCartDropMenuItem
													key={product.orderItemListRow.orderItem.orderItemId}
													thumbnail={product.details.thumbnail}
													name={product.details.name}
													attributes={product.details.attributes}
													offerPrice={product.details.prices.offer || 0}
													displayPrice={product.details.prices.list || 0}
													totalAdjustment={product.orderItemListRow.totalAdjustment.totalAdjustment}
													orderItemPrice={product.orderItemListRow.price.orderItemPrice}
													exhausted={product.availability?.status === false}
													variant="drawer"
												/>
											))}
										</React.Fragment>
									) : (
										<HeaderMiniCartDropMenuItemSkeleton variant="drawer" />
									)}
								</Stack>
								<Divider sx={dividerSX} />
								<OrderSummaryBreakdown
									showTitle={false}
									total={grandTotal}
									shipping={totalShippingCharge}
									discount={totalDiscount}
									subtotal={totalPriceWithoutOffer}
									coupons={totalCoupons}
									isMiniCart
									adjustment={order?.adjustment}
								/>

								{allowLastAddedPartNumber ? (
									<CatalogEntryIARecommendationFree
										params={{ partNumber: allowLastAddedPartNumber }}
										clickSource={CLICK_PRODUCT_VIEW_SOURCE.QUICK_CART_RECOMMENDED}
										partNumbersExtractor={(data) =>
											partNumbersIARecommendationExtractorByUnknown('outfit', data)
										}
										emptyExtractor={iaEmptyExtractor}
										render={(component) => (
											<>
												<Divider
													sx={{
														marginTop: '20px',
														marginBottom: '11px',
														marginX: '-30px',
													}}
												/>
												<Box sx={{ marginTop: '20px', marginRight: '-30px' }}>
													<Typography
														sx={{
															fontSize: 'var(--font-size-body)',
															fontWeight: 800,
															color: '#2C2C2C',
															marginBottom: '11px',
															textTransform: 'uppercase',
														}}
													>
														{'Te puede interesar'}
													</Typography>
													{component}
												</Box>
											</>
										)}
									/>
								) : null}
							</Box>
							<Box sx={footerSX}>
								<Stack direction="row" spacing="14px" alignItems="center" justifyContent={'center'}>
									<ButtonBase sx={cartButtonSX} onClick={handleOnClickCart}>
										{`Ver carrito ${
											orderItemListRowsWithAvailability.length > 0
												? `(${orderItemListRowsWithAvailability.length})`
												: ''
										}`}
									</ButtonBase>
									<ButtonBase
										sx={checkoutButtonSX}
										disabled={disabled}
										onClick={handleOnClickCheckout}
									>
										{'Ir a pagar'}
									</ButtonBase>
								</Stack>
							</Box>
						</Fragment>
					) : (
						<Stack spacing={1} alignItems="center" sx={headerMiniCartItemSX}>
							<Typography variant="body1" align="center">
								{CartLabels.Empty.t()}
							</Typography>
						</Stack>
					)}
				</Box>
			</Drawer>
			<AuthentificationFlow
				open={openAuthFlow.open}
				type="login"
				origin="quick_cart"
				onClose={openAuthFlow.off}
			/>
		</>
	);
};
